// Plan 08-07 — lazy fetch + cache for /locales/{en,sv}.json.
//
// Vendored verbatim from 08-RESEARCH.md §"i18n/load.ts — Lazy Message
// Loading" with one addition: an internal `_clearMessageCacheForTesting`
// hook so unit tests can reset the module-scoped cache between cases.
//
// Cache contract:
//   - `cache` is a Map<locale, parsed dict> populated on first successful
//     fetch. Subsequent calls return the same reference (the test relies
//     on `expect(second).toBe(first)`).
//   - Swedish failures fall back to loadMessages('en') (the default
//     locale) so the app renders SOMETHING even when sv.json is missing
//     (e.g. preview build that hasn't shipped translations yet).
//   - English failures throw — no fallback for the default locale,
//     because there's nothing left to fall back TO.
//
// The /locales/*.json route is cached by the public/_headers rule
// `max-age=300, stale-while-revalidate=86400` so fetch traffic on
// repeat-visit users is essentially free.

const cache = new Map<string, Record<string, string>>()

export async function loadMessages(
  locale: 'en' | 'sv',
): Promise<Record<string, string>> {
  const cached = cache.get(locale)
  if (cached) return cached
  const res = await fetch(`/locales/${locale}.json`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    if (locale === 'en') {
      throw new Error('Default locale missing — /locales/en.json fetch failed')
    }
    return loadMessages('en')
  }
  const data = (await res.json()) as Record<string, string>
  cache.set(locale, data)
  return data
}

/**
 * Test-only: reset the module-scoped cache. Tests reach for this between
 * cases so each case observes a fresh fetch state. Production callers must
 * not depend on this — the cache is stable across the page session.
 */
export function _clearMessageCacheForTesting(): void {
  cache.clear()
}
